/**
 * DirectThreadPage — the drill-in view for a single direct message thread.
 * Uses the same comment posting flow (`/api/comments` with thread_type="direct")
 * as project threads so the compose UX is familiar.
 */
import { useEffect, useState, useRef } from "react";
import { renderRichBody } from "../lib/richText";
import { RichTextarea } from "../components/RichTextarea";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Send, Check, CheckCircle2, Lock, Users, User, Paperclip, X as XIcon, CornerUpLeft,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { getDirectThread, markDirectThreadHandled, uploadFile, API_BASE } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERNAL_ROLES = new Set(["super_admin", "manager", "ops_coordinator"]);

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleString();
}

export default function DirectThreadPage() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  // When the thread load errors (404 / 403 / network), remember the status
  // so we can render a human message instead of a blank page. Feb 2026.
  const [loadError, setLoadError] = useState(null); // { status, message }
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [internalOnly, setInternalOnly] = useState(false);
  const [handling, setHandling] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);  // [{id, filename}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);
  const replyRef = useRef(null);
  const focusReply = () => {
    replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    replyRef.current?.focus();
  };

  const viewerIsInternal = INTERNAL_ROLES.has(user?.role);

  const load = async () => {
    try {
      const { data } = await getDirectThread(threadId);
      setThread(data);
      setLoadError(null);
    } catch (e) {
      const status = e?.response?.status;
      // Keep the toast for seasoned users, but we no longer rely on it as
      // the only signal — the page itself now shows the error state below.
      const message = status === 404
        ? "This message doesn't exist (it may have been removed, or the link came from a different environment)."
        : status === 403
          ? "You don't have access to this direct message."
          : (e?.response?.data?.detail || "Failed to load this message. Please try again.");
      setLoadError({ status: status || 0, message });
      setThread(null);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const send = async () => {
    if (!reply.trim() && pendingAttachments.length === 0) return;
    setPosting(true);
    try {
      await axios.post(`${API}/comments`, {
        thread_type: "direct",
        thread_id: threadId,
        body: reply.trim(),
        file_ids: pendingAttachments.map((a) => a.id),
        internal_only: internalOnly,
      });
      setReply("");
      setInternalOnly(false);
      setPendingAttachments([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply");
    } finally {
      setPosting(false);
    }
  };

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB — skipped`);
          continue;
        }
        const { data } = await uploadFile(file);
        setPendingAttachments((prev) => [...prev, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (id) =>
    setPendingAttachments((prev) => prev.filter((f) => f.id !== id));

  const handleMarkHandled = async () => {
    setHandling(true);
    try {
      await markDirectThreadHandled(threadId);
      setThread((prev) => (prev ? { ...prev, is_handled: true } : prev));
      toast.success("Marked as handled");
    } catch (e) {
      toast.error("Could not mark as handled");
    } finally {
      setHandling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading thread…
      </div>
    );
  }
  if (!thread) {
    // Explicit "we couldn't load this thread" state so an invalid link from
    // an email never shows a bare white content pane under the sidebar.
    return (
      <div className="space-y-4" data-testid="direct-thread-missing">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
          data-testid="direct-thread-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <Card className="p-8 text-center space-y-3 border-amber-200 bg-amber-50/40">
          <div className="mx-auto h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="h-5 w-5 text-amber-700" />
          </div>
          <h2 className="text-lg font-outfit font-semibold">
            {loadError?.status === 404 ? "This message isn't here anymore" : "We couldn't load this message"}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {loadError?.message || "The thread couldn't be loaded. Please try again."}
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button variant="outline" onClick={() => { setLoading(true); load(); }} data-testid="direct-thread-retry">
              Try again
            </Button>
            <Link to="/direct">
              <Button data-testid="direct-thread-open-inbox">Open Direct Messages</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="direct-thread-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        data-testid="direct-thread-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-outfit font-semibold">{thread.subject}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>From {thread.created_by_name}</span>
              <span>·</span>
              <span>{timeAgo(thread.created_at)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                {thread.recipient_type === "agency_broadcast" ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                To: {(thread.recipients || []).map((r) => r.name).join(", ") || "—"}
                {thread.recipient_type === "agency_broadcast" && <Badge variant="outline" className="text-[9px] ml-1 h-4">Broadcast</Badge>}
              </span>
            </div>
          </div>
          {viewerIsInternal && (
            thread.is_handled ? (
              <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50" data-testid="direct-thread-handled-badge">
                <Check className="h-3 w-3 mr-1" /> Handled
              </Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleMarkHandled}
                disabled={handling}
                data-testid="direct-thread-handled-btn"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark handled
              </Button>
            )
          )}
        </div>
      </Card>

      {/* Reply composer */}
      <Card className="p-3 space-y-2" data-testid="reply-composer-top">
        <RichTextarea
          ref={replyRef}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          placeholder="Type your reply…"
          data-testid="direct-thread-reply-input"
        />
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="direct-thread-pending-attachments">
            {pendingAttachments.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary text-[11px] font-medium border"
                data-testid={`direct-thread-pending-attachment-${f.id}`}
              >
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[180px] truncate">{f.filename}</span>
                <button
                  type="button"
                  onClick={() => removePendingAttachment(f.id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${f.filename}`}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleAttach}
          data-testid="direct-thread-file-input"
        />
        <div className="flex items-center justify-between gap-2">
          {viewerIsInternal ? (
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="direct-thread-internal-toggle">
              <input
                type="checkbox"
                checked={internalOnly}
                onChange={(e) => setInternalOnly(e.target.checked)}
                className="rounded"
              />
              <Lock className="h-3 w-3" />
              Internal only — hidden from reps
            </label>
          ) : <div />}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || posting}
              data-testid="direct-thread-attach-btn"
              title="Attach files"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
            <Button
              onClick={send}
              disabled={(!reply.trim() && pendingAttachments.length === 0) || posting}
              data-testid="direct-thread-send-btn"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send
            </Button>
          </div>
        </div>
      </Card>

      {/* Messages */}
      <div className="space-y-2">
        {[...(thread.comments || [])].reverse().map((m, idx) => {
          const mine = m.author_id === user?.id;
          return (
            <div key={idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[78%] rounded-lg px-3 py-2 border ${mine ? "bg-primary text-primary-foreground border-primary/30" : "bg-white"} ${m.internal_only ? "ring-2 ring-amber-400 border-amber-400" : ""}`}
                data-testid={`direct-msg-${idx}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[11px] font-medium ${mine ? "text-primary-foreground/80" : "text-foreground"}`}>
                    {m.author_name || "User"}
                  </span>
                  {m.internal_only && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase bg-amber-500 text-white rounded-full px-1.5 py-0.5"
                      title="Internal only — hidden from reps/clients"
                    >
                      <Lock className="h-2.5 w-2.5" /> Internal
                    </span>
                  )}
                  <span className={`text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {timeAgo(m.created_at)}
                  </span>
                  {!mine && (
                    <button type="button" onClick={focusReply}
                      className={`text-[10px] font-semibold hover:underline inline-flex items-center gap-0.5 ${m.internal_only ? "text-amber-700" : "text-primary"}`}
                      data-testid={`msg-reply-btn-${idx}`}>
                      <CornerUpLeft className="h-3 w-3" /> Reply
                    </button>
                  )}
                </div>
                <div className={`text-sm whitespace-pre-wrap ${mine ? "text-primary-foreground" : ""}`}>
                  {renderRichBody(m.body, { mentions: false })}
                </div>
                {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                  <div className={`mt-2 pt-2 border-t space-y-1 ${mine ? "border-primary-foreground/25" : "border-zinc-200"}`}>
                    {m.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={`${API_BASE}/api/files/${att.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-1.5 text-[11px] font-medium truncate hover:underline ${mine ? "text-primary-foreground" : "text-primary"}`}
                        data-testid={`direct-msg-attachment-${att.id}`}
                        title={`Download ${att.filename}`}
                      >
                        <Paperclip className="h-3 w-3 shrink-0" />
                        <span className="truncate">{att.filename}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

    </div>
  );
}
